<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Tell Us About Your Listings'"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<!-- One address × channel row: brand badge + checkbox, and the ID / target fields once checked. -->
<ng-template #entryCard let-entry="entry">
  <tidy-card class="ls-entry">
    <div class="ls-entry-row">
      <span class="platform-avatar" [ngClass]="entry.platform">
        <img [src]="entry.logo" [alt]="entry.platformLabel" />
      </span>
      <div class="ls-entry-check">
        <tidy-checkbox
          [items]="entry.checkboxItems"
          [initValue]="entry.has"
          [disabled]="entry.locked"
          (checkedChange)="onEntryChecked(entry, $event)" />
        @if (entry.statusNote) {
          <tidy-text [helper]="entry.statusNote" [translate]="false" class="ls-entry-note" />
        }
      </div>
    </div>
    @if (entry.has) {
      <ng-container *ngTemplateOutlet="entryFields; context: { entry: entry }" />
    }
  </tidy-card>
</ng-template>

<ng-template #entryFields let-entry="entry">
  <div class="ls-entry-fields">
    <tidy-input
      [label]="entry.idLabel"
      [formControl]="entry.listingId"
      [form]="entry.listingId"
      [icon]="'assets/svg/home-outline.svg'"
      [submitted]="submitted"
      [errorMessage]="entry.idError" />
    <tidy-select
      [label]="'What are you listing?'"
      [form]="entry.target"
      [items]="targetItems"
      [selectedItem]="entry.targetItem"
      [icon]="'assets/svg/list.svg'"
      (optionChange)="onTargetChange(entry, $event)" />
  </div>
</ng-template>

<ng-template #summaryCard let-item="item" let-handled="handled">
  <tidy-card class="ls-summary-card">
    <div class="ls-summary-row">
      @if (handled) {
        <span class="ls-summary-icon">
          <tidy-image [src]="'assets/svg/construct-outline.svg'" />
        </span>
      } @else {
        <span class="platform-avatar" [ngClass]="item.platform">
          <img [src]="item.logo" [alt]="item.platformLabel" />
        </span>
      }
      <div class="ls-summary-text">
        <tidy-text [helper]="item.addressName" [translate]="false" />
        <tidy-text [body]="item.title" [translate]="false" class="ls-summary-title" />
        @if (item.detail) {
          <tidy-text [helper]="item.detail" [translate]="false" />
        }
      </div>
    </div>
    @if (!handled) {
      @if (item.platform === 'airbnb') {
        <div class="ls-summary-actions">
          <tidy-small-button [text]="'Copy Email'" [image]="'assets/svg/copy-outline.svg'" (buttonClick)="copyEmail()" />
          @if (item.cohostUrl) {
            <tidy-small-button [text]="'Open Co-Hosts Page'" [image]="'assets/svg/open-outline.svg'" (buttonClick)="openCohostPage(item.cohostUrl)" />
          }
        </div>
      } @else {
        <!-- Anything the client has to send us goes to their account manager. -->
        <div class="ls-summary-contact">
          <tidy-text [helper]="item.contactPrompt" [translate]="false" />
          <div class="ls-summary-actions">
            <tidy-small-button [text]="'Call'" [image]="'assets/svg/call-outline.svg'" (buttonClick)="callManager()" />
            <tidy-small-button [text]="'Email'" [image]="'assets/svg/mail-outline.svg'" (buttonClick)="emailManager()" />
            <tidy-small-button [text]="'Text'" [image]="'assets/svg/chatbox-ellipses-outline.svg'" (buttonClick)="textManager()" />
          </div>
        </div>
      }
    }
  </tidy-card>
</ng-template>

<ng-template #content>
  <tidy-wrapper>
    @if (state === 'loading') {
      <div class="loading-container">
        <tidy-loader />
      </div>
    }

    @if (state === 'error') {
      <tidy-alert>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [body]="'Could not load your properties. Please try again.'" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-button [text]="'Try Again'" [action]="retry" class="primary" />
        </tidy-row>
      </tidy-alert>
    }

    @if (state === 'no-property') {
      <div class="ls-intro">
        <tidy-image [src]="'assets/svg/home-outline.svg'" class="ls-intro-icon" />
        <tidy-text [title]="'Add a property first'" />
        <tidy-text [helper]="'Once you have a property, tell us which Airbnb, VRBO, or Booking.com listings you already have and we’ll set up the rest.'" />
      </div>
      <div class="ls-nav-row">
        <tidy-button [text]="'Add Property'" [action]="goToAddProperty" class="primary" />
      </div>
    }

    @if (state === 'form') {
      @if (mode === 'all') {
        <div class="ls-intro">
          <tidy-text [title]="'Which of these listings do you already have?'" />
          <tidy-text [helper]="'Check each site where a property is already listed. We’ll set up the rest for you.'" />
        </div>

        @for (group of groups; track group.addressId) {
          <div class="ls-group">
            <div class="ls-group-header">
              <tidy-text [body]="group.name" [translate]="false" class="ls-group-name" />
              @if (group.city) {
                <tidy-text [helper]="group.city" [translate]="false" />
              }
            </div>
            @for (entry of group.entries; track entry.key) {
              <ng-container *ngTemplateOutlet="entryCard; context: { entry: entry }" />
            }
          </div>
        }
      } @else if (entry) {
        <div class="ls-intro">
          <span class="platform-avatar ls-intro-avatar" [ngClass]="entry.platform">
            <img [src]="entry.logo" [alt]="entry.platformLabel" />
          </span>
          <tidy-text [title]="singleQuestion" [translate]="false" />
        </div>

        <div class="ls-choices">
          <!-- "Yes" holds the listing fields itself once picked, so the answer and its
               details read as one card. -->
          <tidy-card class="ls-choice">
            <div class="ls-choice-row" (click)="selectAnswer('yes')">
              <span class="ls-radio" [class.ls-radio-selected]="answer === 'yes'">
                <span class="ls-radio-inner"></span>
              </span>
              <div class="ls-choice-text">
                <tidy-text [body]="'Yes'" />
                <tidy-text [helper]="'I already have this listing.'" />
              </div>
            </div>
            @if (answer === 'yes') {
              <ng-container *ngTemplateOutlet="entryFields; context: { entry: entry }" />
            }
          </tidy-card>
          <tidy-card class="ls-choice">
            <div class="ls-choice-row" (click)="selectAnswer('no')">
              <span class="ls-radio" [class.ls-radio-selected]="answer === 'no'">
                <span class="ls-radio-inner"></span>
              </span>
              <div class="ls-choice-text">
                <tidy-text [body]="'No'" />
                <tidy-text [helper]="'I don’t have one yet — TIDY will set it up for me.'" />
              </div>
            </div>
          </tidy-card>
        </div>

        @if (submitted && !answer) {
          <tidy-row>
            <tidy-error-message [text]="'Please choose an option.'" />
          </tidy-row>
        }
      }

      <div class="ls-nav-row">
        <tidy-button
          [text]="submitting ? 'Saving…' : 'Next'"
          [action]="submit"
          [disabled]="submitting"
          class="primary" />
      </div>
    }

    @if (state === 'summary') {
      <div class="ls-intro">
        <tidy-image [src]="'assets/svg/checkmark-circle-outline.svg'" class="ls-intro-icon ls-intro-icon-success" />
        <tidy-text [title]="summaryTitle" [translate]="false" />
        @if (summaryIntro) {
          <tidy-text [helper]="summaryIntro" [translate]="false" />
        }
      </div>

      @if (summaryNeeded.length) {
        <div class="ls-section-label">
          <tidy-text [header]="'What we need from you'" />
        </div>
        @for (item of summaryNeeded; track item.addressId + item.platform) {
          <ng-container *ngTemplateOutlet="summaryCard; context: { item: item, handled: false }" />
        }
      }

      @if (summaryHandled.length) {
        <div class="ls-section-label">
          <tidy-text [header]="'What we’ll handle'" />
        </div>
        @for (item of summaryHandled; track item.addressId + item.platform) {
          <ng-container *ngTemplateOutlet="summaryCard; context: { item: item, handled: true }" />
        }
      }

      <div class="ls-nav-row">
        <tidy-button [text]="'OK'" [action]="finish" class="primary" />
      </div>
    }
  </tidy-wrapper>
</ng-template>

@if (!windowService.isDesktopRes) {
  <app-scroll-wrapper [templateContent]="content" />
} @else {
  <ng-container *ngTemplateOutlet="content" />
}
